import { useEffect, useRef } from 'react'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Keyboard behaviour for a modal dialog or sheet: moves focus into the panel
 * when it opens, keeps Tab inside it, closes on Escape, and hands focus back
 * to whatever opened it. Attach the returned ref to the panel element, which
 * should also carry role="dialog", aria-modal and tabIndex={-1}.
 */
export function useDialog<T extends HTMLElement>(active: boolean, onClose: () => void) {
  const ref = useRef<T>(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  // Remember the opener during the render that opens the dialog: by the time
  // the effect runs, an autoFocus field inside the panel may already hold focus.
  const openerRef = useRef<HTMLElement | null>(null)
  const wasActive = useRef(false)
  if (active && !wasActive.current) openerRef.current = document.activeElement as HTMLElement | null
  wasActive.current = active

  useEffect(() => {
    if (!active) return
    const opener = openerRef.current
    const panel = ref.current
    if (panel && !panel.contains(document.activeElement)) panel.focus()

    const focusables = () =>
      panel ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null) : []

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        closeRef.current()
        return
      }
      if (e.key !== 'Tab' || !panel) return
      const items = focusables()
      if (items.length === 0) {
        e.preventDefault()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const current = document.activeElement
      if (e.shiftKey && (current === first || current === panel)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (current === last || !panel.contains(current))) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      // After the panel unmounts, so the opener isn't mid-render.
      requestAnimationFrame(() => {
        if (opener?.isConnected) opener.focus()
      })
    }
  }, [active])

  return ref
}
